// Ricarica veicoli — tipi, etichette e funzioni comuni alle pagine.
// Le forme sono quelle del contratto API (docs/ricarica-veicoli-contratto-api.md, sezione 2).

import { formatDataOraRoma, formatDataRoma } from '../../../utils/dateRoma';

// ── Tipi ─────────────────────────────────────────────────────────────────────

export type StatoConnettore =
  | 'Available' | 'Preparing' | 'Charging' | 'SuspendedEV' | 'SuspendedEVSE'
  | 'Finishing' | 'Reserved' | 'Unavailable' | 'Faulted';

export interface SessioneInCorso {
  id: number;
  transactionId: number;
  dipendente: string | null;
  inizioAt: string;
  energiaWh: number;
}

export interface Connettore {
  numero: number;
  stato: StatoConnettore;
  codiceErrore: string | null;
  sessioneInCorso: SessioneInCorso | null;
}

export interface Colonnina {
  id: number;
  societaId: number;
  societaNome: string;
  nome: string;
  codiceOcpp: string;
  sede: { id: number; nome: string } | null;
  marca: string | null;
  modello: string | null;
  seriale: string | null;
  firmware: string | null;
  collegata: boolean;
  ultimoContattoAt: string | null;
  stato: StatoConnettore | null;
  codiceErrore: string | null;
  attiva: boolean;
  passwordImpostata: boolean;
  connettori: Connettore[];
}

export interface StatoGateway {
  raggiungibile: boolean;
  ultimoContattoAt: string | null;
}

export interface ElencoColonnine {
  gateway: StatoGateway;
  colonnine: Colonnina[];
}

export interface SchedaColonnina {
  gateway: StatoGateway;
  colonnina: Colonnina;
}

export interface ColonninaNome { id: number; nome: string; sede: string | null }

export type TipoComando =
  | 'AVVIO_REMOTO' | 'STOP_REMOTO' | 'RESET' | 'SBLOCCO'
  | 'DISPONIBILITA' | 'RICHIESTA_STATO' | 'LEGGI_CONFIGURAZIONE';

export type StatoComando = 'IN_ATTESA' | 'INVIATO' | 'ACCETTATO' | 'RIFIUTATO' | 'ERRORE' | 'SCADUTO';

export interface Comando {
  id: number;
  tipo: TipoComando;
  parametri: Record<string, string | number | boolean> | null;
  stato: StatoComando;
  risposta: Record<string, unknown> | null;
  messaggio: string | null;
  richiestoDa: string;
  createdAt: string;
  esitoAt: string | null;
}

export interface BadgeAvvio { codice: string; dipendente: string }

export interface Tariffa {
  id: number;
  prezzoKwh: number;
  validaDal: string; // AAAA-MM-GG, ora italiana
  creatoDa: string;
}

export interface Badge {
  id: number;
  codice: string;
  descrizione: string | null;
  dipendente: { id: number; nome: string; cognome: string };
  validoDal: string;
  validoAl: string | null;
  attivo: boolean;
  ultimoUtilizzoAt: string | null;
}

export interface BadgeSconosciuto {
  codiceBadge: string;
  tentativi: number;
  ultimoTentativoAt: string;
  colonnina: { id: number; nome: string; sede: string | null; societaNome: string };
}

export type StatoSessione = 'IN_CORSO' | 'CONCLUSA' | 'ANOMALA';

export interface Sessione {
  id: number;
  transactionId: number;
  colonnina: { id: number; nome: string; societaId: number; societaNome: string; sede: string | null };
  connettore: number;
  dipendente: { id: number; nome: string; cognome: string; societaId: number; societaNome: string } | null;
  codiceBadge: string;
  inizioAt: string;
  fineAt: string | null;
  energiaWh: number | null;
  prezzoKwh: number | null;
  importo: number | null;
  stato: StatoSessione;
  motivoAnomalia: string | null;
  tardiva: boolean;
  competenza: { anno: number; mese: number } | null;
}

export interface ElencoSessioni {
  sessioni: Sessione[];
  troncato: boolean;
  totali: { sessioni: number; energiaWh: number; importo: number; anomale: number };
}

export interface VoceLog { createdAt: string; utenteName: string; azione: string; dettaglio: string | null }

export interface DettaglioSessione extends Sessione {
  meterStartWh: number;
  meterStopWh: number | null;
  motivoStop: string | null;
  ultimoEventoAt: string | null;
  tariffaMancante: boolean;
  log: VoceLog[];
}

export interface RigaRiaddebito {
  dipendente: { id: number; nome: string; cognome: string; codiceFiscale: string | null };
  sessioni: number;
  energiaWh: number;
  importo: number;
  tardive: number;
}

export interface Riaddebito {
  periodo: { stato: 'APERTO' | 'CHIUSO'; chiusoAt: string | null; chiusoDa: string | null };
  righe: RigaRiaddebito[];
  totali: { sessioni: number; energiaWh: number; importo: number };
  avvisi: {
    anomale: number;
    inCorso: number;
    nonCollegate: number;
    precedentiNonChiusi: { anno: number; mese: number; sessioni: number }[];
  };
}

export interface DipendenteOpzione { id: number; nome: string; cognome: string }
export interface SedeOpzione { id: number; nome: string }

// ── Moduli dei permessi (backend/src/config/moduliPermessi.js) ──────────────

export const MOD_COLONNINE = 'aziendale.ricarica.colonnine';
export const MOD_BADGE = 'aziendale.ricarica.badge';
export const MOD_SESSIONI = 'aziendale.ricarica.sessioni';
export const MOD_RIADDEBITI = 'aziendale.ricarica.riaddebiti';

// ── Percorsi delle pagine ───────────────────────────────────────────────────

export const PERCORSO_COLONNINE = '/gestione-aziendale/ricarica/colonnine';
export const PERCORSO_BADGE = '/gestione-aziendale/ricarica/badge';
export const PERCORSO_SESSIONI = '/gestione-aziendale/ricarica/sessioni';
export const PERCORSO_RIADDEBITI = '/gestione-aziendale/ricarica/riaddebiti';

// ── Etichette ────────────────────────────────────────────────────────────────

export const STATO_CONNETTORE: Record<StatoConnettore, { label: string; badge: string }> = {
  Available: { label: 'Libera', badge: 'bop-badge--success' },
  Preparing: { label: 'In preparazione', badge: 'bop-badge--info' },
  Charging: { label: 'In carica', badge: 'bop-badge--info' },
  SuspendedEV: { label: "In pausa dall'auto", badge: 'bop-badge--warning' },
  SuspendedEVSE: { label: 'In pausa dalla colonnina', badge: 'bop-badge--warning' },
  Finishing: { label: 'In chiusura', badge: 'bop-badge--info' },
  Reserved: { label: 'Riservata', badge: 'bop-badge--neutral' },
  Unavailable: { label: 'Fuori servizio', badge: 'bop-badge--neutral' },
  Faulted: { label: 'Guasta', badge: 'bop-badge--danger' },
};

export const TIPO_COMANDO: Record<TipoComando, string> = {
  AVVIO_REMOTO: 'Avvio da remoto',
  STOP_REMOTO: 'Stop da remoto',
  RESET: 'Riavvio',
  SBLOCCO: 'Sblocco presa',
  DISPONIBILITA: 'Disponibilità',
  RICHIESTA_STATO: 'Aggiornamento stato',
  LEGGI_CONFIGURAZIONE: 'Lettura configurazione',
};

export const STATO_COMANDO: Record<StatoComando, { label: string; badge: string }> = {
  IN_ATTESA: { label: 'In attesa', badge: 'bop-badge--neutral' },
  INVIATO: { label: 'Inviato', badge: 'bop-badge--info' },
  ACCETTATO: { label: 'Eseguito', badge: 'bop-badge--success' },
  RIFIUTATO: { label: 'Rifiutato', badge: 'bop-badge--warning' },
  ERRORE: { label: 'Errore', badge: 'bop-badge--danger' },
  SCADUTO: { label: 'Scaduto', badge: 'bop-badge--neutral' },
};

export const STATO_SESSIONE: Record<StatoSessione, { label: string; badge: string }> = {
  IN_CORSO: { label: 'In corso', badge: 'bop-badge--info' },
  CONCLUSA: { label: 'Conclusa', badge: 'bop-badge--success' },
  ANOMALA: { label: 'Anomala', badge: 'bop-badge--danger' },
};

export const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

// ── Funzioni ─────────────────────────────────────────────────────────────────

export function formatDataOra(iso: string | null): string {
  return iso ? formatDataOraRoma(iso) : '—';
}

export function formatData(iso: string | null): string {
  return formatDataRoma(iso);
}

// Giorno AAAA-MM-GG (senza ora) mostrato come GG/MM/AAAA.
export function formatGiorno(giorno: string | null): string {
  if (!giorno) return '—';
  const [a, m, g] = giorno.split('-');
  return `${g}/${m}/${a}`;
}

export function whInKwh(wh: number): number {
  return wh / 1000;
}

export function etichettaMese(anno: number, mese: number): string {
  return `${MESI[mese - 1]} ${anno}`;
}

// Messaggio di errore leggibile da un errore di apiFetch.
export function messaggioErrore(err: unknown, ripiego: string): string {
  if (err instanceof Error && err.message) return err.message;
  return ripiego;
}
